/**
 * FIDO2SDK - Main SDK Class
 *
 * The primary entry point for FIDO2-JS SDK. This class wraps all core modules
 * and provides a clean, high-level API for FIDO2 biometric authentication.
 *
 * @module sdk
 */

import { SDKConfig, PartialSDKConfig } from './types/config';
import { mergeConfig } from './config/defaults';
import { APIClient } from './core/api';
import {
  createCredential,
  getCredential,
  WebAuthnError,
  WebAuthnErrorCode,
  type Credential,
  type AuthenticationResult
} from './core/webauthn';
import { parseVerifiedSummary, type VerifiedSummary } from './core/verified-summary';
import { fetchAssertionPayload, DEFAULT_LOOPBACK_URL } from './core/loopback';
import { checkAAGUID, checkAAGUIDAny, type AAGUIDVerificationResult } from './core/verifier';
import { checkWebAuthnSupport, detectPlatform } from './utils/platform';
import { base64ToBuffer } from './utils/buffer';
import type { CustomOptions } from './core/encoder';
import {
  generateEnrollmentDeeplink,
  generateVerificationDeeplink,
  generateIOSStartDeeplink,
  generateAndroidInitDeeplink,
  generateAndroidResolveDeeplink,
  buildReturnURLWithParams,
  parseReturnURLStatus,
  isDeeplinkSupported
} from './core/deeplink';
import {
  savePendingOperation,
  getPendingOperation,
  clearPendingOperation,
  hasPendingOperation,
  type SessionState
} from './utils/session';

/**
 * Verification mode for authentication results
 * Matches iOS SDK VerificationMode enum
 */
export type VerificationMode =
  | 'none'       // No verification - just return JWE token
  | 'default' // Use built-in default endpoint and API key
  | { custom: { endpoint: string; apiKey: string } }; // Custom endpoint and API key

/**
 * Options for registering a new credential
 */
export interface RegisterOptions {
  /**
   * Username for the credential
   */
  username: string;

  /**
   * Relying party domain (e.g., 'example.com')
   */
  domain: string;

  /**
   * Optional display name for the user
   */
  displayName?: string;

  /**
   * Optional authenticator name to verify (e.g., 'ultrapass')
   * If provided, will verify the AAGUID matches the configured value
   */
  authenticator?: string;

  /**
   * Optional custom challenge for registration (base64-encoded string)
   * If not provided, a random challenge will be generated
   */
  challenge?: string;

  /**
   * Optional user ID (base64-encoded string)
   * Will be decoded to binary data internally
   * If not provided, a random user ID will be generated
   * Note: Should be base64-encoded binary data (like iOS SDK), typically received from backend
   */
  userId?: string;

  /**
   * Optional relying party name
   */
  rpName?: string;

  /**
   * Optional timeout in milliseconds
   */
  timeout?: number;

  /**
   * Enable mobile deeplink flow (two-phase authentication)
   * When true, redirects to native app for biometric capture (Phase 1)
   * then completes WebAuthn ceremony on callback (Phase 2)
   *
   * Only available on iOS and Android platforms
   * @default false
   */
  useMobileFlow?: boolean;

  /**
   * Return URL for mobile deeplink callback
   * Required if useMobileFlow is true
   * The mobile app will redirect back to this URL after biometric capture
   *
   * @example 'https://example.com/auth/callback'
   */
  returnURL?: string;

  /**
   * Public key that unlocks `encryptedEmbedding` on the result, for local silent
   * re-verification. Passed on the iOS enroll deeplink and stored by the app for the ceremony.
   *
   * This SDK has no key manager, so supply the key yourself; without it enrollment still
   * succeeds but no encrypted embedding is ever produced.
   *
   * @platform iOS
   */
  verifyPublicKey?: string;

  /**
   * Caller-supplied age, used by the app to adapt enrollment — for instance skipping the
   * document scan for under-16 users. Typically computed from a birthday collected at sign-up.
   *
   * @platform iOS
   */
  userAge?: number;

  /**
   * Optional authenticator attachment constraint.
   * - 'cross-platform': Roaming/external authenticator (USB HID) — use for physical UltraPass device on desktop
   * - 'platform': Built-in authenticator (Face ID, Touch ID, Windows Hello, Android biometrics)
   * - undefined: No constraint — browser offers all available authenticators (default)
   */
  authenticatorAttachment?: AuthenticatorAttachment;
}

/**
 * Result from successful registration
 */
export interface RegisterResult extends Credential {
  /**
   * AAGUID verification result (if authenticator was specified)
   */
  verification?: AAGUIDVerificationResult;
}

/**
 * Options for authenticating with an existing credential
 */
export interface AuthenticateOptions {
  /**
   * Relying party domain (e.g., 'example.com')
   */
  domain: string;

  /**
   * Custom authentication options (liveness, age, geo-location checks)
   */
  customOptions?: CustomOptions;

  /**
   * Verification mode for JWE token
   *
   * - 'none': No verification - just return JWE token (default)
   * - 'default': Use built-in default endpoint and API key
   * - { custom: { endpoint, apiKey } }: Use custom endpoint
   *
   * @default 'none'
   *
   * @example
   * ```typescript
   * // No verification
   * verification: 'none'
   *
   * // Use default endpoint (recommended)
   * verification: 'default'
   *
   * // Custom endpoint
   * verification: {
   *   custom: {
   *     endpoint: 'https://api.example.com/verify',
   *     apiKey: 'your-api-key'
   *   }
   * }
   * ```
   */
  verification?: VerificationMode;

  /**
   * Optional challenge for authentication (base64-encoded string)
   * If not provided, a random challenge will be generated
   */
  challenge?: string;

  /**
   * Optional array of allowed credential IDs (base64-encoded strings)
   */
  allowCredentials?: string[];

  /**
   * Optional timeout in milliseconds
   */
  timeout?: number;

  /**
   * Enable mobile deeplink flow (two-phase authentication)
   * When true, redirects to native app for biometric capture (Phase 1)
   * then completes WebAuthn ceremony on callback (Phase 2)
   *
   * Only available on iOS and Android platforms
   * @default false
   */
  useMobileFlow?: boolean;

  /**
   * Return URL for mobile deeplink callback
   * Required if useMobileFlow is true
   * The mobile app will redirect back to this URL after biometric capture
   *
   * @example 'https://example.com/auth/callback'
   */
  returnURL?: string;

  /**
   * Specific credential ID to verify against (base64-encoded)
   * Used in mobile flows to specify which credential to use
   */
  credentialId?: string;

  /**
   * User ID associated with the credential (base64-encoded)
   * Used in mobile flows for user identification
   */
  userId?: string;

  /**
   * Username to match a local credential against, sent on the iOS `start` deeplink.
   *
   * Also echoed on the return URL so the callback can recover which user was authenticating
   * when sessionStorage does not survive the redirect.
   */
  username?: string;

  /**
   * Pre-select the biometric modality on iOS, skipping UltraPass's auth-method picker.
   *
   * Recognised by both the `start` host and the `mode=verify` branch of the app; anything
   * other than 'face' or 'voice' is ignored there.
   *
   * @platform iOS
   */
  verifyMethod?: 'face' | 'voice';

  /**
   * Allow UltraPass to fall back to enrollment when no local credential matches, instead of
   * returning `credential_invalid`.
   *
   * Defaults to false — an explicit `authenticate()` should fail rather than silently turn
   * into a registration, which is the class of bug that stale-operation handling exists to
   * catch. Set true only if you want `ultrapass://start` to behave as a combined
   * sign-in-or-register entry point.
   *
   * @default false
   * @platform iOS (`start` path only)
   */
  allowEnrollFallback?: boolean;
}

/**
 * Result from successful authentication
 */
export interface AuthenticateResult extends AuthenticationResult {
  /**
   * Raw verify response from the backend, when verification ran.
   *
   * Prefer {@link verifiedSummary} for typed access; use this for fields the summary
   * does not surface.
   */
  verifiedData?: unknown;

  /**
   * Typed projection over the verify response, or null when verification was skipped.
   *
   * Gate access on `verifiedSummary.allChecksPassed` rather than on the absence of a
   * thrown error: a verify call can succeed at the HTTP level while reporting that face
   * match or liveness failed.
   *
   * @example
   * ```typescript
   * const result = await sdk.authenticate({ domain, verification: 'default' });
   * if (result.verifiedSummary?.allChecksPassed) {
   *   grantAccess();
   * } else {
   *   denyAccess(result.verifiedSummary?.errorDescription ?? 'checks did not pass');
   * }
   * ```
   */
  verifiedSummary?: VerifiedSummary | null;
}

/**
 * Main FIDO2SDK class
 *
 * This class provides the primary API for FIDO2 biometric authentication.
 * It integrates all core modules (API client, WebAuthn, verifier, encoder)
 * and provides a clean, type-safe interface.
 *
 * @example
 * ```typescript
 * // Initialize SDK
 * const sdk = new FIDO2SDK({
 *   apiKey: 'your-api-key'
 * });
 *
 * await sdk.init();
 *
 * // Register a new credential
 * const credential = await sdk.register({
 *   username: 'john.doe',
 *   domain: 'example.com',
 *   authenticator: 'ultrapass'
 * });
 *
 * // Authenticate
 * const result = await sdk.authenticate({
 *   domain: 'example.com',
 *   customOptions: {
 *     checkLiveness: true,
 *     checkAge: true,
 *     ageThreshold: 18
 *   }
 * });
 *
 * if (result.verifiedData) {
 *   console.log('Authentication successful:', result.verifiedData);
 * }
 * ```
 */
export class FIDO2SDK {
  /**
   * SDK configuration
   */
  private config: SDKConfig;

  /**
   * API client for backend communication
   */
  private api: APIClient;

  /**
   * Creates a new FIDO2SDK instance
   *
   * @param userConfig - Partial SDK configuration (apiKey is required)
   *
   * @throws {Error} If apiKey is not provided
   *
   * @example
   * ```typescript
   * const sdk = new FIDO2SDK({
   *   apiKey: 'your-api-key',
   *   timeout: 30000,
   *   debug: true
   * });
   * ```
   */
  constructor(userConfig: PartialSDKConfig) {
    // Validate required apiKey
    if (!userConfig.apiKey || typeof userConfig.apiKey !== 'string') {
      throw new Error('FIDO2SDK: apiKey is required and must be a string');
    }

    // Merge user config with defaults
    this.config = mergeConfig(userConfig);

    // Create API client
    this.api = new APIClient(this.config);

    if (this.config.debug) {
      console.log('[FIDO2-JS] SDK initialized with config:', {
        apiEndpoints: this.config.apiEndpoints,
        authenticators: Object.keys(this.config.authenticators),
        timeout: this.config.timeout,
        debug: this.config.debug
      });
    }
  }

  /**
   * Resolves the `'auto'` ceremony options against the current platform.
   *
   * Four of our WebAuthn options diverge from the PingFederate adapter's templates
   * (`ultrapass-pf-adapter`, branch `feat/usernameless-auth`) — the reference implementation
   * known to work on Windows. It requests no PRF, no credProtect keys, and no `transports` hint,
   * and it enforces the timeout with an AbortController because Windows ignores
   * `publicKey.timeout`.
   *
   * `'auto'` therefore means: match the PF adapter on desktop, and keep today's behaviour on the
   * deeplink platforms, where PRF is how custom biometric options reach UltraPass and the timing
   * is tuned (see the iOS Phase 2 notes in `docs/`). Each knob is independently overridable so a
   * Windows failure can be bisected rather than guessed at.
   *
   * @returns Concrete values to hand to `createCredential` / `getCredential`
   */
  private resolveCeremonyOptions(): {
    requestPrf: boolean;
    requestCredProtect: boolean;
    transports: AuthenticatorTransport[] | null;
    enforceTimeoutWithAbort: boolean;
    fetchAssertionPayload?: (clientDataJSON: string) => Promise<string | null>;
  } {
    const usesDeeplink = isDeeplinkSupported(detectPlatform());

    const prfSetting = this.config.prfExtension || 'auto';
    const credProtectSetting = this.config.credProtectExtension ?? 'auto';
    const transportsSetting = this.config.allowCredentialsTransports || 'auto';
    const abortSetting = this.config.enforceTimeoutWithAbort ?? 'auto';
    const loopbackSetting = this.config.loopbackPayloadFallback ?? 'auto';

    const useLoopback = loopbackSetting === 'auto' ? !usesDeeplink : loopbackSetting === true;

    const resolved = {
      requestPrf: prfSetting === 'auto' ? usesDeeplink : prfSetting === 'always',
      requestCredProtect:
        credProtectSetting === 'auto' ? usesDeeplink : credProtectSetting === true,
      transports:
        transportsSetting === 'auto'
          ? usesDeeplink
            ? (['internal', 'hybrid', 'usb'] as AuthenticatorTransport[])
            : null
          : transportsSetting === 'none'
            ? null
            : transportsSetting,
      enforceTimeoutWithAbort: abortSetting === 'auto' ? !usesDeeplink : abortSetting === true,
      // Bound to config here so core/webauthn stays free of transport concerns; it only sees a
      // callback it may or may not have been given.
      ...(useLoopback && {
        fetchAssertionPayload: (clientDataJSON: string) =>
          fetchAssertionPayload(clientDataJSON, {
            url: this.config.loopbackURL,
            timeout: this.config.loopbackTimeout,
            debug: this.config.debug,
          }),
      }),
    };

    if (this.config.debug) {
      console.log('[FIDO2-JS] Ceremony options resolved:', {
        usesDeeplink,
        ...resolved,
        transports: resolved.transports || '(omitted — PF-adapter behaviour)',
        fetchAssertionPayload: useLoopback
          ? `enabled (${this.config.loopbackURL || DEFAULT_LOOPBACK_URL}/payload)`
          : 'disabled',
      });
    }

    return resolved;
  }

  /**
   * Registers a new FIDO2 credential
   *
   * This method creates a new WebAuthn credential and optionally verifies
   * the authenticator AAGUID against the configured value.
   *
   * Works in standalone mode - challenges and userIds are auto-generated.
   *
   * @param options - Registration options
   * @returns Promise that resolves to the created credential
   *
   * @throws {Error} If WebAuthn is not supported
   * @throws {WebAuthnError} If credential creation fails
   * @throws {Error} If AAGUID verification fails (when authenticator specified)
   *
   * @example
   * ```typescript
   * // Standalone mode - no init needed
   * const credential = await sdk.register({
   *   username: 'john.doe',
   *   domain: 'example.com'
   * });
   *
   * // With AAGUID verification
   * const credential = await sdk.register({
   *   username: 'john.doe',
   *   domain: 'example.com',
   *   authenticator: 'ultrapass'
   * });
   * ```
   */
  async register(options: RegisterOptions): Promise<RegisterResult> {
    this.ensureWebAuthnSupported();

    if (this.config.debug) {
      console.log('[FIDO2-JS] Starting registration:', {
        username: options.username,
        domain: options.domain,
        authenticator: options.authenticator,
        useMobileFlow: options.useMobileFlow
      });
    }

    // Handle mobile deeplink flow
    if (options.useMobileFlow) {
      const platform = detectPlatform();

      if (!isDeeplinkSupported(platform)) {
        throw new Error(
          `Mobile deeplink flow is not supported on platform: ${platform}. ` +
            'Deeplink flow is only available on iOS and Android.'
        );
      }

      // Android: Init + Direct WebAuthn pattern (like webauthn_v2.js)
      if (platform === 'Android') {
        if (this.config.debug) {
          console.log('[FIDO2-JS] Android detected: using init + direct WebAuthn flow');
        }

        // Generate init deeplink
        const initDeeplink = generateAndroidInitDeeplink(
          this.config.apiKey || '',
          this.config.policyId || 'mobile'
        );

        if (this.config.debug) {
          console.log('[FIDO2-JS] Opening Android app for initialization:', initDeeplink);
        }

        // this.config is already merged with DEFAULT_CONFIG, so the ?? only covers a caller
        // that explicitly passed undefined.
        const initDelay = this.config.androidInitDelay ?? 1000;

        // Try to open the app (non-blocking)
        // This initializes the UltraPass app but doesn't wait for callback
        if (typeof window !== 'undefined') {
          // Create a hidden iframe to trigger the deeplink without full page redirect
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = initDeeplink;
          document.body.appendChild(iframe);

          // Clean up iframe after a moment
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, initDelay);
        }

        // Give the app a moment to initialize. Too short and the ceremony can start while the
        // UltraPass app is still cold starting, which Android's Credential Manager surfaces as
        // NotReadableError.
        await new Promise(resolve => setTimeout(resolve, initDelay));

        if (this.config.debug) {
          console.log('[FIDO2-JS] Proceeding with direct WebAuthn registration');
        }

        // Fall through to direct WebAuthn flow below
        // (Don't use two-phase callback pattern for Android)
      } else {
        // iOS: Two-phase callback pattern
        if (!options.returnURL) {
          throw new Error(
            'returnURL is required when useMobileFlow is true on iOS. ' +
              'Provide a HTTPS URL where the mobile app should redirect after biometric capture.'
          );
        }

        if (this.config.debug) {
          console.log('[FIDO2-JS] iOS detected: using two-phase deeplink flow');
        }

        // Save pending operation to sessionStorage
        const sessionState: SessionState = {
          operation: 'enroll',
          username: options.username,
          domain: options.domain,
          displayName: options.displayName,
          challenge: options.challenge,
          userId: options.userId,
          timeout: options.timeout,
          timestamp: Date.now()
        };

        savePendingOperation(sessionState);

        // Generate iOS deeplink. generateEnrollmentDeeplink stamps op/username/apiKey onto the
        // return URL itself, so no pre-processing is needed here.
        const deeplink = generateEnrollmentDeeplink(platform, {
          username: options.username,
          rp: options.domain,
          returnURL: options.returnURL,
          policyId: this.config.policyId || 'mobile',
          apiKey: this.config.apiKey || '',
          userId: options.userId,
          displayName: options.displayName,
          verifyPublicKey: options.verifyPublicKey,
          userAge: options.userAge,
          scheme: this.config.ultrapassScheme
        });

        if (this.config.debug) {
          console.log('[FIDO2-JS] Redirecting to iOS deeplink:', deeplink);
        }

        // Redirect to mobile app
        if (typeof window !== 'undefined') {
          window.location.href = deeplink;
        }

        // Return a pending promise that will never resolve
        // The actual registration will complete in handleDeeplinkCallback after redirect
        return new Promise(() => {});
      }
    }

    // Direct WebAuthn flow (no mobile redirect)
    // Create credential (convert string parameters to Uint8Array)
    const ceremony = this.resolveCeremonyOptions();

    const credential = await createCredential({
      username: options.username,
      domain: options.domain,
      displayName: options.displayName,
      challenge: options.challenge ? new Uint8Array(base64ToBuffer(options.challenge)) : undefined,
      userId: options.userId ? new Uint8Array(base64ToBuffer(options.userId)) : undefined,
      rpName: options.rpName,
      timeout: options.timeout || this.config.timeout,
      authenticatorAttachment: options.authenticatorAttachment,
      largeBlobSupport: this.config.largeBlobSupport,
      largeBlobExtension: this.config.largeBlobExtension,
      requestPrf: ceremony.requestPrf,
      requestCredProtect: ceremony.requestCredProtect,
      enforceTimeoutWithAbort: ceremony.enforceTimeoutWithAbort
    });

    if (this.config.debug) {
      console.log('[FIDO2-JS] Credential created:', credential.credentialId);
      console.log('[FIDO2-JS] largeBlob supported by this credential:', credential.largeBlobSupported);
    }

    // Verify AAGUID if authenticator specified
    let verification: AAGUIDVerificationResult | undefined;

    if (options.authenticator) {
      // Pass the platform we actually ran on rather than letting the verifier re-detect it, so
      // the AAGUID set checked here cannot disagree with the one the ceremony was built for.
      verification = checkAAGUID(
        base64ToBuffer(credential.attestationObject),
        options.authenticator,
        this.config,
        detectPlatform()
      );

      if (!verification.matches) {
        throw new Error(
          `Authenticator AAGUID mismatch: expected ${verification.targetHex}, got ${verification.foundHex} ` +
            `(platform: ${detectPlatform()}). The authenticator used does not match the expected ` +
            `"${options.authenticator}" authenticator. AAGUIDs are configured per platform — see ` +
            'authenticators.<name>.platformAaguids.'
        );
      }

      if (this.config.debug) {
        console.log('[FIDO2-JS] AAGUID verification passed:', verification.authenticatorName);
      }
    }

    return {
      ...credential,
      verification
    };
  }

  /**
   * Authenticates with an existing FIDO2 credential
   *
   * This method retrieves a WebAuthn credential and optionally verifies
   * the JWE token with the backend. Custom options (liveness, age, geo-location)
   * can be encoded and passed to the authenticator via the PRF extension.
   *
   * Works in standalone mode - challenges are auto-generated.
   *
   * @param options - Authentication options
   * @returns Promise that resolves to the authentication result
   *
   * @throws {Error} If WebAuthn is not supported
   * @throws {WebAuthnError} If credential retrieval fails
   * @throws {APIError} If backend verification fails (when verification is enabled)
   *
   * @example
   * ```typescript
   * // Option 1: No verification (default)
   * const result = await sdk.authenticate({
   *   domain: 'example.com'
   * });
   * // result.jweToken available, no verification
   *
   * // Option 2: Use default verification (recommended)
   * const result = await sdk.authenticate({
   *   domain: 'example.com',
   *   verification: 'default'
   * });
   * // result.verifiedData contains decoded user data
   *
   * // Option 3: Custom verification endpoint
   * const result = await sdk.authenticate({
   *   domain: 'example.com',
   *   verification: {
   *     custom: {
   *       endpoint: 'https://api.example.com/verify',
   *       apiKey: 'your-api-key'
   *     }
   *   }
   * });
   *
   * // With custom biometric options
   * const result = await sdk.authenticate({
   *   domain: 'example.com',
   *   customOptions: {
   *     checkLiveness: true,
   *     checkAge: true,
   *     ageThreshold: 18
   *   },
   *   verification: 'default'
   * });
   *
   * if (result.verifiedData) {
   *   console.log('User data:', result.verifiedData);
   * }
   * ```
   */
  async authenticate(options: AuthenticateOptions): Promise<AuthenticateResult> {
    this.ensureWebAuthnSupported();

    if (this.config.debug) {
      console.log('[FIDO2-JS] Starting authentication:', {
        domain: options.domain,
        customOptions: options.customOptions,
        verification: options.verification || 'none',
        useMobileFlow: options.useMobileFlow
      });
    }

    // Handle mobile deeplink flow
    if (options.useMobileFlow) {
      const platform = detectPlatform();

      if (!isDeeplinkSupported(platform)) {
        throw new Error(
          `Mobile deeplink flow is not supported on platform: ${platform}. ` +
            'Deeplink flow is only available on iOS and Android.'
        );
      }

      // Android: Init + Direct WebAuthn pattern (like webauthn_v2.js)
      if (platform === 'Android') {
        if (this.config.debug) {
          console.log('[FIDO2-JS] Android detected: using init + direct WebAuthn flow');
        }

        // Generate init deeplink
        const initDeeplink = generateAndroidInitDeeplink(
          this.config.apiKey || '',
          this.config.policyId || 'mobile'
        );

        if (this.config.debug) {
          console.log('[FIDO2-JS] Opening Android app for initialization:', initDeeplink);
        }

        // this.config is already merged with DEFAULT_CONFIG, so the ?? only covers a caller
        // that explicitly passed undefined.
        const initDelay = this.config.androidInitDelay ?? 1000;

        // Try to open the app (non-blocking)
        // This initializes the UltraPass app but doesn't wait for callback
        if (typeof window !== 'undefined') {
          // Create a hidden iframe to trigger the deeplink without full page redirect
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = initDeeplink;
          document.body.appendChild(iframe);

          // Clean up iframe after a moment
          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, initDelay);
        }

        // Give the app a moment to initialize. Too short and the ceremony can start while the
        // UltraPass app is still cold starting, which Android's Credential Manager surfaces as
        // NotReadableError.
        await new Promise(resolve => setTimeout(resolve, initDelay));

        if (this.config.debug) {
          console.log('[FIDO2-JS] Proceeding with direct WebAuthn authentication');
        }

        // Fall through to direct WebAuthn flow below
        // (Don't use two-phase callback pattern for Android)
      } else {
        // iOS: Two-phase callback pattern
        if (!options.returnURL) {
          throw new Error(
            'returnURL is required when useMobileFlow is true on iOS. ' +
              'Provide a HTTPS URL where the mobile app should redirect after biometric capture.'
          );
        }

        if (this.config.debug) {
          console.log('[FIDO2-JS] iOS detected: using two-phase deeplink flow');
        }

        // Save pending operation to sessionStorage
        const sessionState: SessionState = {
          operation: 'verify',
          username: options.username,
          domain: options.domain,
          customOptions: options.customOptions,
          verification: options.verification,
          challenge: options.challenge,
          allowCredentials: options.allowCredentials,
          credentialId: options.credentialId,
          userId: options.userId,
          timeout: options.timeout,
          timestamp: Date.now()
        };

        savePendingOperation(sessionState);

        // Generate iOS deeplink.
        //
        // Default to the unified `start` host: it is the current entry point, reads `options`
        // (so custom biometric options survive), takes `credentialIds` as a CSV, and reports
        // back which operation it ran via `mode` on the return URL.
        //
        // `verifyOnly: true` because this is an explicit sign-in — if no local credential
        // matches, the app should return `credential_invalid` rather than quietly enrolling
        // the user, which would turn an authenticate() call into a registration.
        const useStartPath = (this.config.iosAuthPath || 'start') === 'start';

        // The start builder is called directly rather than through generateVerificationDeeplink,
        // so the return URL has to be stamped here. This is the fallback for the iOS Safari case
        // where sessionStorage does not survive the redirect (notably a new-tab callback);
        // `username` matters because the callback uses it to recover the credential from
        // localStorage. The legacy branch below gets the same treatment inside its dispatcher.
        //
        // The start flow additionally appends its own `mode=`, reporting what it actually ran,
        // which takes priority on the way back.
        const deeplink = useStartPath
          ? generateIOSStartDeeplink({
              rpId: options.domain,
              returnURL: buildReturnURLWithParams(options.returnURL, 'verify', {
                username: options.username,
                apiKey: this.config.apiKey,
                verification:
                  typeof options.verification === 'string' ? options.verification : undefined
              }),
              policyId: this.config.policyId || 'mobile',
              apiKey: this.config.apiKey || '',
              username: options.username,
              userId: options.userId,
              credentialIds:
                options.allowCredentials ||
                (options.credentialId ? [options.credentialId] : undefined),
              options: options.customOptions,
              verifyOnly: options.allowEnrollFallback !== true,
              verifyMethod: options.verifyMethod,
              scheme: this.config.ultrapassScheme
            })
          : generateVerificationDeeplink(platform, {
              rpId: options.domain,
              returnURL: options.returnURL,
              policyId: this.config.policyId || 'mobile',
              apiKey: this.config.apiKey || '',
              credentialId: options.credentialId,
              userId: options.userId,
              options: options.customOptions,
              // Only pass verification if it's a string (URL params can't encode objects)
              verification:
                typeof options.verification === 'string' ? options.verification : 'default',
              scheme: this.config.ultrapassScheme
            });

        if (this.config.debug) {
          console.log('[FIDO2-JS] Redirecting to iOS deeplink:', deeplink);
        }

        // Redirect to mobile app
        if (typeof window !== 'undefined') {
          window.location.href = deeplink;
        }

        // Return a pending promise that will never resolve
        // The actual authentication will complete in handleDeeplinkCallback after redirect
        return new Promise(() => {});
      }
    }

    // Direct WebAuthn flow (no mobile redirect)
    // Get credential (convert string parameters to Uint8Array)
    const ceremony = this.resolveCeremonyOptions();

    const result = await getCredential({
      domain: options.domain,
      customOptions: options.customOptions,
      challenge: options.challenge ? new Uint8Array(base64ToBuffer(options.challenge)) : undefined,
      allowedCredentials: options.allowCredentials,
      timeout: options.timeout || this.config.timeout,
      requestPrf: ceremony.requestPrf,
      transports: ceremony.transports,
      enforceTimeoutWithAbort: ceremony.enforceTimeoutWithAbort,
      fetchAssertionPayload: ceremony.fetchAssertionPayload
    });

    if (this.config.debug) {
      console.log('[FIDO2-JS] Credential retrieved:', {
        credentialId: result.credentialId,
        hasJweToken: !!result.jweToken,
        hasPrfResults: !!result.prfResults
      });
    }

    // Handle verification based on mode
    let verifiedData: unknown;
    let verifiedSummary: VerifiedSummary | null = null;
    const verificationMode = options.verification || 'none';

    if (verificationMode !== 'none') {
      if (this.config.debug) {
        console.log('[FIDO2-JS] Verification mode:', verificationMode);
      }

      // Verification was asked for but no biometric proof arrived. Previously this fell
      // through and resolved successfully with verifiedData undefined, which reads as
      // "face verified" to a caller that only checks for a thrown error. Fail instead.
      if (!result.verifyPayload) {
        // Several unrelated situations produce an empty blob, and this used to assert the
        // Windows one as though it were the explanation — which reads as nonsense on a Mac
        // authenticating over the cross-device QR, where loopback and HID are both irrelevant.
        // List them as candidates instead, most likely first for the running platform.
        const platform = detectPlatform();

        const crossDevice =
          'Cross-device (the QR / "use a phone or tablet" flow): largeBlob is not reliably ' +
          'carried over the hybrid transport, so the assertion can succeed while the blob ' +
          'comes back empty. Run the ceremony directly on the device holding the credential ' +
          'instead of driving it from another machine.';
        const windowsLoopback =
          'Windows plugin-authenticator transport: it does not carry largeBlob at all, so the ' +
          'JWE has to come over the loopback /payload route — and a page served from a PUBLIC ' +
          'origin (an https trycloudflare/ngrok tunnel) is blocked by the browser from reaching ' +
          'loopback, whatever the authenticator does. Serve the page from http://localhost on ' +
          'that machine, or use the HID transport, where largeBlob does cross.';
        const noBlobOnCredential =
          'The credential itself may have no blob — check the registration log for an ' +
          'unconfirmed largeBlob, and that the UltraPass app completed a face enrollment for ' +
          'this user. If so, re-register; authenticating again will not help.';
        const wrongAuthenticator = 'The authenticator that answered may not be UltraPass.';

        const causes =
          platform === 'Desktop'
            ? [crossDevice, windowsLoopback, noBlobOnCredential, wrongAuthenticator]
            : [noBlobOnCredential, crossDevice, wrongAuthenticator];

        throw new WebAuthnError(
          WebAuthnErrorCode.RETRIEVAL_FAILED,
          'Verification was requested but the assertion carried no biometric proof ' +
            `(no largeBlob payload). On ${platform}, in order of likelihood: ` +
            causes.map((cause, i) => `(${i + 1}) ${cause}`).join(' '),
          { credentialId: result.credentialId }
        );
      }

      // Determine endpoint and API key based on verification mode
      let endpoint: string;
      let apiKey: string;

      if (verificationMode === 'default') {
        // Use default endpoint from config
        endpoint = this.config.apiEndpoints.verify;
        apiKey = this.config.apiKey || '';

        if (this.config.debug) {
          console.log('[FIDO2-JS] Using default verification endpoint:', endpoint);
        }
      } else if (typeof verificationMode === 'object' && verificationMode.custom) {
        // Use custom endpoint
        endpoint = verificationMode.custom.endpoint;
        apiKey = verificationMode.custom.apiKey;

        if (this.config.debug) {
          console.log('[FIDO2-JS] Using custom verification endpoint:', endpoint);
        }
      } else {
        throw new Error(
          `Invalid verification mode: ${JSON.stringify(verificationMode)}. ` +
            "Expected 'none', 'default', or { custom: { endpoint, apiKey } }."
        );
      }

      // Send the full payload as the raw body — see APIClient.verifyPayload for why this
      // is not the same as posting result.jweToken.
      try {
        const verifyResponse = await this.api.verifyPayload(
          result.verifyPayload,
          endpoint,
          apiKey
        );
        verifiedData = verifyResponse;
        verifiedSummary = parseVerifiedSummary(verifyResponse);

        if (this.config.debug) {
          console.log('[FIDO2-JS] Verification complete:', {
            verified: verifiedSummary?.verified,
            faceMatch: verifiedSummary?.faceMatch,
            livenessPass: verifiedSummary?.livenessPass,
            agePass: verifiedSummary?.agePass,
            allChecksPassed: verifiedSummary?.allChecksPassed
          });
        }
      } catch (error) {
        if (this.config.debug) {
          console.error('[FIDO2-JS] Verification failed:', error);
        }
        throw error;
      }
    }

    return {
      ...result,
      verifiedData,
      verifiedSummary
    };
  }

  /**
   * Handles deeplink callback from mobile app
   *
   * This method should be called after the mobile app redirects back to your website
   * following the biometric capture (Phase 1). It will:
   * 1. Check the return URL status (success/error/cancelled)
   * 2. Retrieve the pending operation from sessionStorage
   * 3. Complete the WebAuthn ceremony (Phase 2) if successful
   * 4. Clean up session state
   *
   * **Usage Pattern:**
   * ```typescript
   * // On your callback page
   * const sdk = new FIDO2SDK({ apiKey: 'your-api-key' });
   *
   * if (sdk.hasPendingOperation()) {
   *   const result = await sdk.handleDeeplinkCallback();
   *   if (result) {
   *     console.log('Authentication successful:', result);
   *   }
   * }
   * ```
   *
   * @param url - Optional URL to parse (defaults to current window.location)
   * @returns Promise that resolves to the result (RegisterResult or AuthenticateResult), or null if no pending operation
   *
   * @throws {Error} If status is error or cancelled
   * @throws {Error} If WebAuthn ceremony fails
   *
   * @example
   * ```typescript
   * // Automatic handling on page load
   * window.addEventListener('load', async () => {
   *   const sdk = new FIDO2SDK({ apiKey: 'your-api-key' });
   *
   *   try {
   *     const result = await sdk.handleDeeplinkCallback();
   *     if (result) {
   *       document.getElementById('status').textContent = 'Success!';
   *     }
   *   } catch (error) {
   *     document.getElementById('status').textContent = `Error: ${error.message}`;
   *   }
   * });
   * ```
   */
  async handleDeeplinkCallback(url?: string): Promise<RegisterResult | AuthenticateResult | null> {
    // Check if there's a pending operation in sessionStorage
    const hasPending = hasPendingOperation();

    // Parse return URL status
    const status = parseReturnURLStatus(url);

    if (this.config.debug) {
      console.log('[FIDO2-JS] Deeplink callback status:', status);
      console.log('[FIDO2-JS] Has pending operation in sessionStorage:', hasPending);
    }

    // CRITICAL FIX: Parse URL params FIRST to check for operation type
    // URL params are the source of truth since they come from the deeplink we generated
    // sessionStorage might have stale data from a previous operation
    let urlOperation: 'enroll' | 'verify' | null = null;
    let urlUsername: string | undefined;
    let urlApiKey: string | undefined;
    let urlVerification: string | undefined;

    if (typeof window !== 'undefined') {
      try {
        const urlObj = new URL(url || window.location.href);
        const op = urlObj.searchParams.get('op');
        urlUsername = urlObj.searchParams.get('username') || undefined;
        urlApiKey = urlObj.searchParams.get('apiKey') || undefined;
        urlVerification = urlObj.searchParams.get('verification') || undefined;

        if (op === 'enroll' || op === 'verify') {
          urlOperation = op;
        }

        // The unified `start` flow appends `mode=verify` / `mode=enroll` reporting which
        // operation UltraPass actually performed. That is the app's own account of what
        // happened, so it outranks `op`, which only records what the browser asked for. This
        // is what makes `start` immune to the enroll/verify mismatch class of bug — the
        // browser no longer has to guess correctly up front.
        if (status.mode) {
          if (this.config.debug && urlOperation && status.mode !== urlOperation) {
            console.log(
              `[FIDO2-JS] UltraPass reports mode=${status.mode} (requested op=${urlOperation}) — trusting the app`
            );
          }
          urlOperation = status.mode;
        }

        if (this.config.debug) {
          console.log('[FIDO2-JS] URL params:', {
            op: urlOperation,
            mode: status.mode,
            username: urlUsername,
            apiKey: urlApiKey ? '***' : undefined,
            verification: urlVerification
          });
        }
      } catch (error) {
        if (this.config.debug) {
          console.warn('[FIDO2-JS] Failed to parse URL params:', error);
        }
      }
    }

    // Retrieve pending operation from sessionStorage
    let pendingOp = getPendingOperation();

    if (this.config.debug && pendingOp) {
      console.log('[FIDO2-JS] sessionStorage operation type:', pendingOp.operation);
    }

    // CRITICAL: If URL has operation type that differs from sessionStorage, prioritize URL
    // This fixes the bug where stale sessionStorage from previous operation causes wrong flow
    if (urlOperation && pendingOp && urlOperation !== pendingOp.operation) {
      if (this.config.debug) {
        console.warn('[FIDO2-JS] ⚠️ Operation type mismatch detected!');
        console.warn('[FIDO2-JS] URL says:', urlOperation, '(source of truth)');
        console.warn('[FIDO2-JS] sessionStorage says:', pendingOp.operation, '(stale data)');
        console.warn('[FIDO2-JS] Discarding sessionStorage and using URL params');
      }

      // Discard stale sessionStorage
      pendingOp = null;
    }

    // If no valid pending operation, build from URL params
    if (!pendingOp && urlOperation && typeof window !== 'undefined') {
      if (this.config.debug) {
        console.log('[FIDO2-JS] Building operation from URL params:', urlOperation);
      }

      pendingOp = {
        operation: urlOperation,
        username: urlUsername,
        domain: window.location.host, // Use host (includes port) to match POC
        verification: (urlVerification as 'default' | 'none') || 'default',
        timestamp: Date.now()
      };

      // For authentication, try to recover allowCredentials from localStorage
      // This is critical for new tab scenarios where sessionStorage is lost
      if (urlOperation === 'verify' && urlUsername && typeof localStorage !== 'undefined') {
        try {
          const storedCreds = localStorage.getItem('fido2_credentials');
          if (storedCreds) {
            const creds = JSON.parse(storedCreds);
            if (creds[urlUsername] && creds[urlUsername].credentialId) {
              pendingOp.allowCredentials = [creds[urlUsername].credentialId];

              if (this.config.debug) {
                console.log('[FIDO2-JS] Recovered credential ID from localStorage for username:', urlUsername);
                console.log('[FIDO2-JS] Credential ID:', creds[urlUsername].credentialId.substring(0, 50) + '...');
              }
            } else if (this.config.debug) {
              console.warn('[FIDO2-JS] No stored credential found for username:', urlUsername);
            }
          }
        } catch (error) {
          if (this.config.debug) {
            console.warn('[FIDO2-JS] Failed to recover credential from localStorage:', error);
          }
        }
      }

      // Update SDK config with apiKey from URL if available
      if (urlApiKey && urlApiKey !== this.config.apiKey) {
        if (this.config.debug) {
          console.log('[FIDO2-JS] Recovered apiKey from URL params');
        }
        this.updateConfig({ apiKey: urlApiKey });
      }

      if (this.config.debug) {
        console.log('[FIDO2-JS] Built operation from URL params:', {
          operation: pendingOp.operation,
          username: pendingOp.username,
          domain: pendingOp.domain,
          hasCredentials: !!pendingOp.allowCredentials
        });
      }
    }

    // If still no pending operation, return null
    if (!pendingOp) {
      if (this.config.debug) {
        console.log('[FIDO2-JS] No pending operation found (checked sessionStorage and URL params)');
      }
      return null;
    }

    // Clear pending operation from storage
    clearPendingOperation();

    // Handle error/cancelled status
    if (!status.success) {
      if (status.cancelled) {
        throw new Error('Operation was cancelled by the user');
      }
      throw new Error(status.error || 'Biometric verification failed');
    }

    // Success - Wait before Phase 2 (iOS Safari needs time to render)
    // Based on iOS dev reference implementation which uses 3-second countdown
    const delay = this.config.mobileCallbackDelay ?? 3000;

    if (delay > 0) {
      if (this.config.debug) {
        console.log(`[FIDO2-JS] Waiting ${delay}ms before Phase 2 (allows Safari to render)...`);
      }

      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // Force repaint to ensure Safari finishes rendering before WebAuthn
    // Based on POC single-page pattern (index.html lines 913-914)
    // Critical for iOS Safari stability when triggering WebAuthn after deeplink redirect
    if (typeof window !== 'undefined') {
      if (this.config.debug) {
        console.log('[FIDO2-JS] Forcing repaint before WebAuthn...');
      }
      window.scrollTo(0, 0);
      document.body.offsetHeight;
    }

    // Sanitize username (iOS edge case: UltraPass may return URL in username field)
    let username = pendingOp.username || '';
    if (username.startsWith('http://') || username.startsWith('https://')) {
      if (this.config.debug) {
        console.warn('[FIDO2-JS] Username appears to be a URL, rejecting:', username);
      }
      username = '';
    }

    // Complete Phase 2 (WebAuthn ceremony)
    if (pendingOp.operation === 'enroll') {
      // Validate username for enrollment
      if (!username) {
        throw new Error(
          'Username is required for enrollment. ' +
            'The username was not found in sessionStorage or URL params.'
        );
      }

      if (this.config.debug) {
        console.log('[FIDO2-JS] Completing registration (Phase 2)');
      }

      return await this.register({
        username,
        domain: pendingOp.domain,
        displayName: pendingOp.displayName,
        challenge: pendingOp.challenge,
        userId: pendingOp.userId,
        timeout: pendingOp.timeout
      });
    } else if (pendingOp.operation === 'verify') {
      // An Android resolve preflight is a different situation from an iOS Phase 2: no biometric
      // has been captured yet, and UltraPass has just told us exactly which credential it found
      // on the device. Passing that through is useful there.
      //
      // On iOS the opposite holds — allowCredentials must be omitted so resident-key discovery
      // completes without re-prompting for a face scan, since the scan already happened in
      // Phase 1. Hence the narrow condition rather than a general change.
      const resolvedCredentialId =
        status.resolve?.status === 'authenticate' ? status.resolve.credentialId : undefined;

      // Credential selection for Phase 2, in priority order:
      //   1. The credential the Android resolve preflight explicitly named.
      //   2. The stored credential for this operation, when phase2AllowCredentials is on.
      //   3. Nothing — pure resident-key discovery.
      //
      // (3) is the default. Registration requests `residentKey: 'required'`, so the credential is
      // always discoverable and discovery cannot fail for lack of a resident key — which was the
      // only reason (2) was ever the default. Naming a credential explicitly instead makes the
      // UltraPass provider re-verify the biometric that Phase 1 already captured, which surfaces
      // a second face prompt or an outright NotAllowedError. Set `phase2AllowCredentials: true`
      // to restore (2).
      //
      // (1) still wins when present: the Android preflight resolves a specific credential, and
      // that is a positive identification rather than a guess.
      const storedCredentials =
        this.config.phase2AllowCredentials !== false
          ? pendingOp.allowCredentials ||
            (pendingOp.credentialId ? [pendingOp.credentialId] : undefined)
          : undefined;

      const phase2Credentials = resolvedCredentialId ? [resolvedCredentialId] : storedCredentials;

      if (this.config.debug) {
        console.log('[FIDO2-JS] Completing authentication (Phase 2)');
        if (resolvedCredentialId) {
          console.log(
            `[FIDO2-JS] Using credential named by the Android preflight: ${resolvedCredentialId.substring(0, 20)}...`
          );
        } else if (phase2Credentials?.length) {
          console.log(
            `[FIDO2-JS] Sending ${phase2Credentials.length} allowCredential(s) in Phase 2 (phase2AllowCredentials)`
          );
        } else {
          console.log('[FIDO2-JS] No allowCredentials - relying on resident key discovery');
        }
      }

      return await this.authenticate({
        domain: pendingOp.domain,
        customOptions: pendingOp.customOptions,
        verification: pendingOp.verification,
        challenge: pendingOp.challenge,
        allowCredentials: phase2Credentials,
        timeout: pendingOp.timeout
      });
    }

    return null;
  }

  /**
   * Runs Android's `resolve_profile_and_credentials` preflight.
   *
   * This is Android's analogue of the iOS `start` flow: a **scan-free** check that asks
   * UltraPass whether a credential for this RP already exists on the device, so the browser
   * learns whether to register or authenticate instead of guessing. The app's own docs call it
   * the thing to use "instead of `VERIFY_CREDENTIALS` before register/authenticate".
   *
   * Like the iOS deeplink flows this redirects the page and does not resolve — UltraPass sends
   * the outcome back to `returnURL`, where {@link handleDeeplinkCallback} picks it up and runs
   * the ceremony the app selected. No biometric is captured during the preflight; the face scan
   * happens later inside the WebAuthn ceremony.
   *
   * This is opt-in and does not change the default Android flow, which reaches UltraPass through
   * a non-redirecting init deeplink followed by a direct WebAuthn ceremony.
   *
   * @param options - Resolve options
   * @returns A promise that never resolves (the page redirects)
   * @throws {Error} If called on a platform other than Android, or without a returnURL
   *
   * @example
   * ```typescript
   * // Before showing sign-in vs register buttons:
   * await sdk.resolveAndroidCredentials({
   *   domain: window.location.host,
   *   returnURL: window.location.href,
   *   credentialIds: knownCredentialIds
   * });
   * // ...page redirects; on return, handleDeeplinkCallback() runs the right ceremony.
   * ```
   */
  async resolveAndroidCredentials(options: {
    domain: string;
    returnURL: string;
    credentialIds?: string[];
    username?: string;
  }): Promise<never> {
    const platform = detectPlatform();

    if (platform !== 'Android') {
      throw new Error(
        `resolveAndroidCredentials() is Android-only, but the platform is ${platform}. ` +
          'On iOS use authenticate({ useMobileFlow: true }), which routes through ultrapass://start.'
      );
    }

    if (!options.returnURL) {
      throw new Error(
        'returnURL is required for the Android resolve preflight — UltraPass reports the outcome by redirecting to it.'
      );
    }

    // Carry username and apiKey across the redirect, but deliberately NOT `op`: which
    // operation to run is exactly what the preflight is being asked to determine, so
    // pre-committing to one here would defeat the purpose. UltraPass supplies it as
    // `status=authenticate|register`, which parseReturnURLStatus maps onto `mode`.
    let returnURL = options.returnURL;
    try {
      const url = new URL(options.returnURL);
      if (options.username) {
        url.searchParams.set('username', options.username);
      }
      if (this.config.apiKey) {
        url.searchParams.set('apiKey', this.config.apiKey);
      }
      returnURL = url.toString();
    } catch (error) {
      if (this.config.debug) {
        console.warn('[FIDO2-JS] Could not augment resolve return URL:', error);
      }
    }

    const deeplink = generateAndroidResolveDeeplink({
      apiKey: this.config.apiKey || '',
      policyId: this.config.policyId || 'mobile',
      rpId: options.domain,
      returnURL,
      credentialIds: options.credentialIds
    });

    if (this.config.debug) {
      console.log('[FIDO2-JS] Android resolve preflight:', deeplink);
    }

    if (typeof window !== 'undefined') {
      window.location.href = deeplink;
    }

    // The page is navigating away; the flow continues in handleDeeplinkCallback.
    return new Promise<never>(() => {});
  }

  /**
   * Checks if there is a pending mobile deeplink operation
   *
   * Use this to detect if the current page load is a callback from a mobile app.
   *
   * @returns true if there is a pending operation
   *
   * @example
   * ```typescript
   * if (sdk.hasPendingOperation()) {
   *   await sdk.handleDeeplinkCallback();
   * }
   * ```
   */
  hasPendingOperation(): boolean {
    return hasPendingOperation();
  }

  /**
   * Updates the SDK configuration
   *
   * This method allows updating the configuration after SDK creation.
   * The API client will be recreated with the new configuration.
   *
   * @param newConfig - Partial configuration to merge with current config
   *
   * @example
   * ```typescript
   * sdk.updateConfig({
   *   timeout: 30000,
   *   debug: true
   * });
   * ```
   */
  updateConfig(newConfig: Partial<SDKConfig>): void {
    // Merge new config with current config
    // Ensure apiKey is preserved from current config if not provided
    const configToMerge: PartialSDKConfig = {
      apiKey: this.config.apiKey!,
      ...this.config,
      ...newConfig
    };

    // Switching environment must actually retarget the verify endpoint. The current config
    // always carries a resolved `apiEndpoints.verify`, and an explicit endpoint outranks the
    // environment in mergeConfig — so carrying it over would silently pin the old host.
    // Drop it and let the new environment resolve, unless this call names an endpoint.
    const switchingEnvironment =
      newConfig.environment !== undefined && newConfig.environment !== this.config.environment;

    if (switchingEnvironment && !newConfig.apiEndpoints?.verify) {
      configToMerge.apiEndpoints = undefined;
    }

    // Same for the iOS app scheme — a carried-over value would keep pointing at the old
    // environment's UltraPass build, which is worse than the endpoint case because the
    // deeplink then opens the wrong app (or nothing) with no error.
    if (switchingEnvironment && !newConfig.ultrapassScheme) {
      configToMerge.ultrapassScheme = undefined;
    }

    this.config = mergeConfig(configToMerge);

    // Recreate API client with new config
    this.api = new APIClient(this.config);

    if (this.config.debug) {
      console.log('[FIDO2-JS] Config updated:', {
        apiEndpoints: this.config.apiEndpoints,
        authenticators: Object.keys(this.config.authenticators),
        timeout: this.config.timeout,
        debug: this.config.debug
      });
    }
  }

  /**
   * Gets a read-only copy of the current configuration
   *
   * @returns Frozen copy of the SDK configuration
   *
   * @example
   * ```typescript
   * const config = sdk.getConfig();
   * console.log('Timeout:', config.timeout);
   * ```
   */
  getConfig(): Readonly<SDKConfig> {
    return Object.freeze({ ...this.config });
  }

  /**
   * Verifies an AAGUID without registration
   *
   * This is a utility method to check if an attestation object contains
   * a specific authenticator AAGUID.
   *
   * @param attestationObject - Base64-encoded attestation object
   * @param authenticatorName - Name of authenticator to verify against
   * @returns AAGUID verification result
   *
   * @throws {VerificationError} If verification fails
   *
   * @example
   * ```typescript
   * const verification = sdk.verifyAAGUID(
   *   credential.attestationObject,
   *   'ultrapass'
   * );
   *
   * if (verification.matches) {
   *   console.log('Authenticator verified!');
   * }
   * ```
   */
  verifyAAGUID(
    attestationObject: string,
    authenticatorName: string
  ): AAGUIDVerificationResult {
    return checkAAGUID(base64ToBuffer(attestationObject), authenticatorName, this.config);
  }

  /**
   * Verifies an AAGUID against any configured authenticator
   *
   * This is a utility method to check if an attestation object contains
   * any of the configured authenticator AAGUIDs.
   *
   * @param attestationObject - Base64-encoded attestation object
   * @returns AAGUID verification result
   *
   * @throws {VerificationError} If verification fails
   *
   * @example
   * ```typescript
   * const verification = sdk.verifyAAGUIDAny(credential.attestationObject);
   *
   * if (verification.matches) {
   *   console.log('Matched authenticator:', verification.authenticatorName);
   * }
   * ```
   */
  verifyAAGUIDAny(attestationObject: string): AAGUIDVerificationResult {
    return checkAAGUIDAny(base64ToBuffer(attestationObject), this.config);
  }

  /**
   * Ensures WebAuthn is supported before operations
   *
   * @throws {Error} If WebAuthn is not supported
   * @private
   */
  private ensureWebAuthnSupported(): void {
    if (!checkWebAuthnSupport()) {
      throw new Error(
        'WebAuthn is not supported in this browser. ' +
          'Please use a modern browser with WebAuthn support (Chrome 67+, Firefox 60+, Safari 13+, Edge 18+).'
      );
    }
  }
}
